import { getNumberOfOwnersFromFormValues } from '@components/Apply/OwnersMoreDetailsAboutYou/getNumberOfOwners';
import { SSNSchema } from '@components/validation/ssn';
import { RootState } from '@store/reducers';
import { z } from 'zod';

// BCM Short
export const STEP_BCM_2_HOMEPAGE = '/bcm';
export const STEP_BCM_2_INFO = '/bcm/app/info';
export const STEP_BCM_2_BUSINESS_MORE_DETAILS = '/bcm/app/business/more-details';
export const STEP_BCM_2_ADD_CARD = '/bcm/app/add-card';
export const STEP_BCM_2_END_AS_WILL_CONTACT = '/bcm/app/contact-soon';
// BCM
export const STEP_BCM_HOMEPAGE = '/bcm2';
export const STEP_BCM_INFO = '/bcm2/app/info';
export const STEP_BCM_BUSINESS_MORE_DETAILS = '/bcm2/app/business/more-details';
export const STEP_BCM_SUMMARY = '/bcm2/app/summary';
// General
export const STEP_INTEGRATED_LOGIN = '/login';
export const STEP_VERIFY_LOGIN = '/app/verify/login';
// Apply
export const STEP_HOMEPAGE = '/';
export const STEP_AMOUNT = '/amount';
export const STEP_INFO = '/info';
export const STEP_BUSINESS_MORE_DETAILS = '/business/more-details';
export const STEP_BUSINESS_FURTHER_DETAILS = '/business/further-details';
export const STEP_BUSINESS_OWNERS = '/business/owners';
export const STEP_BANK_CONNECT = '/bank-connect';
export const STEP_TYPE_OF_FUNDING = '/type-of-funding';
export const STEP_WHEN_FUNDING_NEEDED = '/when-funding-needed';
export const STEP_SUMMARY = '/funding-is-almost-here';
// EQ Lead Flow
export const STEP_EQ_LEAD_NOT_READY = '/embed/learn-more';
export const STEP_EQ_LEAD_YOUR_INFORMATION = '/eq/your-information';
export const STEP_EQ_LEAD_HOMEPAGE = '/eq';
export const STEP_EQ_LEAD_PRODUCT_DETAILS = '/eq/product-details';
export const STEP_EQ_LEAD_YOUR_BUSINESS = '/eq/your-business';
export const STEP_EQ_LEAD_OWNERS_INFORMATION = '/eq/owners';
export const STEP_EQ_LEAD_SUMMARY = '/eq/summary';

export const APPLY_GENERAL_STEPS = [STEP_INTEGRATED_LOGIN, STEP_VERIFY_LOGIN];

export const APPLY_EQ_LEAD_STEPS = [
  STEP_EQ_LEAD_HOMEPAGE,
  STEP_EQ_LEAD_YOUR_INFORMATION,
  STEP_EQ_LEAD_PRODUCT_DETAILS,
  STEP_EQ_LEAD_YOUR_BUSINESS,
  STEP_EQ_LEAD_OWNERS_INFORMATION,
  STEP_EQ_LEAD_SUMMARY,
];

export const APPLY_LOAN_STEPS = [
  STEP_HOMEPAGE,
  STEP_AMOUNT,
  STEP_INFO,
  STEP_BUSINESS_MORE_DETAILS,
  STEP_BUSINESS_FURTHER_DETAILS,
  // Financing-type screens come BEFORE the owners screen so the wizard
  // timeline progression is monotonic vs. the labeled phases declared in
  // `WizardShell/LoanTimeline.tsx` (Business → Tax → Financing Type →
  // Owner → Review). Before this reorder the owners screen lived between
  // Tax and Bank Connect, which made the timeline appear to bounce
  // between phase 3 → phase 5 → phase 4 → phase 5 as the user advanced.
  STEP_BANK_CONNECT,
  STEP_TYPE_OF_FUNDING,
  STEP_BUSINESS_OWNERS,
  // STEP_WHEN_FUNDING_NEEDED removed per Apply QA — the question is now
  // collected at a later stage. Route + component still exist so any
  // existing deep links continue to render, but the wizard skips it.
  STEP_SUMMARY,
  '/app/select',
  '/app/create',
  '/app/contact-soon',
];

export const APPLY_BCM_STEPS = [
  STEP_BCM_HOMEPAGE,
  STEP_BCM_INFO,
  STEP_BCM_BUSINESS_MORE_DETAILS,
  STEP_BCM_SUMMARY,
  '/bcm2/business/select',
  '/bcm2/app/create',
  '/bcm2/app/contact-soon',
];

export const APPLY_BCM_STEPS_SHORT = [
  STEP_BCM_2_HOMEPAGE,
  STEP_BCM_2_INFO,
  STEP_BCM_2_BUSINESS_MORE_DETAILS,
  STEP_BCM_2_END_AS_WILL_CONTACT,
  '/bcm/business/select',
  '/bcm/app/create',
  '/bcm/app/contact-soon',
];

export const stringWithContent = (message?: string): [(v: string | null | undefined) => boolean, string] => [
  (v: string | null | undefined) => (v ? v.trim().length > 0 : true),
  message || "This field can't be blank.",
];

export type ApplyWizardStep =
  // BCM Short:
  | typeof STEP_BCM_2_HOMEPAGE
  | typeof STEP_BCM_2_INFO
  | typeof STEP_BCM_2_BUSINESS_MORE_DETAILS
  | typeof STEP_BCM_2_ADD_CARD
  | typeof STEP_BCM_2_END_AS_WILL_CONTACT
  // BCM:
  | typeof STEP_BCM_HOMEPAGE
  | typeof STEP_BCM_INFO
  | typeof STEP_INTEGRATED_LOGIN
  | typeof STEP_BCM_BUSINESS_MORE_DETAILS
  | typeof STEP_BCM_SUMMARY
  // Apply:
  | typeof STEP_HOMEPAGE
  | typeof STEP_AMOUNT
  | typeof STEP_INFO
  | typeof STEP_VERIFY_LOGIN
  | typeof STEP_BUSINESS_MORE_DETAILS
  | typeof STEP_BUSINESS_FURTHER_DETAILS
  | typeof STEP_BUSINESS_OWNERS
  | typeof STEP_BANK_CONNECT
  | typeof STEP_TYPE_OF_FUNDING
  | typeof STEP_WHEN_FUNDING_NEEDED
  | typeof STEP_SUMMARY
  // EQ Lead Flow:
  | typeof STEP_EQ_LEAD_NOT_READY
  | typeof STEP_EQ_LEAD_HOMEPAGE
  | typeof STEP_EQ_LEAD_YOUR_INFORMATION
  | typeof STEP_EQ_LEAD_PRODUCT_DETAILS
  | typeof STEP_EQ_LEAD_YOUR_BUSINESS
  | typeof STEP_EQ_LEAD_OWNERS_INFORMATION
  | typeof STEP_EQ_LEAD_SUMMARY;

export type ApplyWizardStepObject = {
  id: ApplyWizardStep;
  isHidden: boolean;
  isShowingInNavigation: boolean;
  path: string;
  zodSchema: z.ZodType<any>;
  isComplete: (state: RootState['form']) => boolean;
  Component: React.ComponentType<any>;
};

export const FIELD_VALIDATION = {
  loan_amount: z.number().positive(),
  loan_terms: z.number().min(1, 'Please select a loan term.'),
  business_name: z
    .string()
    .min(2, 'Please enter a valid business name.')
    .refine(...stringWithContent()),
  owner_1_first: z
    .string()
    .min(2, 'Please enter a valid first name.')
    .refine(...stringWithContent()),
  owner_1_last: z
    .string()
    .min(2, 'Please enter a valid last name.')
    .refine(...stringWithContent()),
  owner_1_mobile: z
    .string()
    .refine((value) => !(value && !/^\d{3}-\d{3}-\d{4}$/i.test(value)), 'Please enter a 10-digit phone number.')
    .refine(...stringWithContent()),
  owner_1_email: z.email('Please enter a valid email address.').refine(...stringWithContent()),
  owner_1_title: z
    .string()
    .min(2, 'Please enter a valid title.')
    .refine(...stringWithContent()),
  owner_1_street: z
    .string()
    .min(2, 'Please enter a valid street address.')
    .refine(...stringWithContent()),
  owner_1_suite: z
    .string()
    .optional()
    .refine(...stringWithContent()),
  owner_1_city: z
    .string()
    .min(2, 'Please enter a valid city.')
    .refine(...stringWithContent()),
  owner_1_state: z
    .string()
    .min(2, 'Please enter a valid state.')
    .refine(...stringWithContent()),
  owner_1_zip: z
    .string()
    .min(2, 'Please enter a valid ZIP code.')
    .refine(...stringWithContent()),
  owner_dob_day: z.number(),
  owner_dob_month: z.number(),
  owner_dob_year: z.number(),
  owner_ownership: z.number().max(100, "Ownership percentage can't be more than 100%."),
  owner_ssn: SSNSchema.refine((value) => !!value?.trim(), 'Please enter your Social Security number.').refine(
    (value) => !value?.trim() || /^(\d{3}|\*{3})-(\d{2}|\*{2})-\d{4}$/i.test(value),
    'Please enter your 9-digit Social Security number (XXX-XX-XXXX).'
  ),
  business_years: z.number(),
  business_revenue: z.number().positive(),
  business_phone: z
    .string()
    .refine((value) => !(value && !/^\d{3}-\d{3}-\d{4}$/i.test(value)), 'Please enter a 10-digit phone number.'),
  business_street: z
    .string()
    .min(2, 'Please enter a valid street address.')
    .refine(...stringWithContent()),
  business_city: z
    .string()
    .min(2, 'Please enter a valid city.')
    .refine(...stringWithContent()),
  business_state: z
    .string()
    .min(2, 'Please enter a valid state.')
    .refine(...stringWithContent()),
  business_zip: z
    .string()
    .min(2, 'Please enter a valid ZIP code.')
    .refine(...stringWithContent()),
  business_suite: z
    .string()
    .optional()
    .refine(...stringWithContent()),
  home_based: z.boolean().optional(),
  // naics_keyword: z.string().min(2, 'Please enter a valid NAICS keyword.'),
  business_naics: z.number(),
  business_structure: z.string().refine(...stringWithContent()),
  business_ein: z
    .string()
    .min(2, 'Please enter your EIN.')
    .refine(...stringWithContent())
    .refine(
      (value) => value && (/^\d{2}-\d{7}$/i.test(value) || /^\d{9}$/i.test(value)),
      'Please enter a valid 9-digit EIN, for example 12-3456789.'
    ),
  business_website: z
    .string()
    .min(2, 'Please enter a valid website.')
    .optional()
    .refine((value) => {
      if (!((value ?? '').trim().length > 0)) {
        return true;
      }

      // Test the schema with various URLs
      // const urls = [
      //   "tento.co",
      //   "https://chatgpt.com/",
      //   "chatgpt.com",
      //   "www.google.com",
      //   "www.google.com/test",
      //   "tento.co/1/2/index.html",
      //   "12.a223.co",
      //   "http://localhost:3000/app/business/more-details",
      //   "sub.domain.example.com",
      //   "http://sub.domain.example.com/path",
      //   "http://web.com:3000/app/business/more-details"
      // ];
      const urlPattern =
        /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}|localhost)(:\d{1,5})?(\/[a-zA-Z0-9-./]*)?$/;
      return urlPattern.test(value?.trim() ?? '');
    }, 'Please enter a valid website.'),
  equipment_description: z.string().optional(),
};

export const ApplyWizardSteps: Record<ApplyWizardStep, Omit<ApplyWizardStepObject, 'Component'>> = {
  [STEP_EQ_LEAD_NOT_READY]: {
    id: STEP_EQ_LEAD_NOT_READY,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_NOT_READY,
    zodSchema: z.object({
      owner_1_phone: FIELD_VALIDATION.owner_1_mobile,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_NOT_READY].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  // EQ Lead Flow
  [STEP_EQ_LEAD_HOMEPAGE]: {
    id: STEP_EQ_LEAD_HOMEPAGE,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_HOMEPAGE,
    zodSchema: z.object({}),
    isComplete: () => true,
  },
  [STEP_EQ_LEAD_YOUR_INFORMATION]: {
    id: STEP_EQ_LEAD_YOUR_INFORMATION,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_YOUR_INFORMATION,
    zodSchema: z.object({
      consent: z.boolean().refine((value: boolean) => value, 'Consent is required to proceed.'),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_YOUR_INFORMATION].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_EQ_LEAD_PRODUCT_DETAILS]: {
    id: STEP_EQ_LEAD_PRODUCT_DETAILS,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_PRODUCT_DETAILS,
    zodSchema: z.object({
      loan_amount: FIELD_VALIDATION.loan_amount,
      equipment_description: z.string(),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_PRODUCT_DETAILS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_EQ_LEAD_YOUR_BUSINESS]: {
    id: STEP_EQ_LEAD_YOUR_BUSINESS,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_YOUR_BUSINESS,
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,

      business_website: FIELD_VALIDATION.business_website,

      business_street: FIELD_VALIDATION.business_street,
      business_suite: FIELD_VALIDATION.business_suite,
      business_city: FIELD_VALIDATION.business_city,
      business_state: FIELD_VALIDATION.business_state,
      business_zip: FIELD_VALIDATION.business_zip,

      business_years: FIELD_VALIDATION.business_years,
      business_revenue: FIELD_VALIDATION.business_revenue,

      business_ein: FIELD_VALIDATION.business_ein,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_YOUR_BUSINESS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_EQ_LEAD_OWNERS_INFORMATION]: {
    id: STEP_EQ_LEAD_OWNERS_INFORMATION,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_OWNERS_INFORMATION,
    zodSchema: {
      parse: (data: { [a: string]: any }) => {
        const ownersCount = getNumberOfOwnersFromFormValues(data);
        let schema: { [a: string]: any } = {};
        for (let i = 1; i <= ownersCount; i++) {
          schema[`owner_${i}_ssn`] = FIELD_VALIDATION.owner_ssn;
          schema[`owner_${i}_title`] = FIELD_VALIDATION.owner_1_title;
          schema[`owner_${i}_ownership`] = FIELD_VALIDATION.owner_ownership;
          schema[`owner_${i}_dob_day`] = FIELD_VALIDATION.owner_dob_day;
          schema[`owner_${i}_dob_month`] = FIELD_VALIDATION.owner_dob_month;
          schema[`owner_${i}_dob_year`] = FIELD_VALIDATION.owner_dob_year;
          schema[`owner_${i}_street`] = FIELD_VALIDATION.owner_1_street;
          schema[`owner_${i}_suite`] = FIELD_VALIDATION.owner_1_suite;
          schema[`owner_${i}_city`] = FIELD_VALIDATION.owner_1_city;
          schema[`owner_${i}_state`] = FIELD_VALIDATION.owner_1_state;
          schema[`owner_${i}_zip`] = FIELD_VALIDATION.owner_1_zip;
          if (i > 1 || (i === 1 && data.isNotAnOwner)) {
            schema[`owner_${i}_mobile`] = FIELD_VALIDATION.owner_1_mobile;
            schema[`owner_${i}_email`] = FIELD_VALIDATION.owner_1_email;
            schema[`owner_${i}_first`] = FIELD_VALIDATION.owner_1_first;
            schema[`owner_${i}_last`] = FIELD_VALIDATION.owner_1_last;
          }
        }
        return z.object(schema).parse(data);
      },
    } as z.ZodType,
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_OWNERS_INFORMATION].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_EQ_LEAD_SUMMARY]: {
    id: STEP_EQ_LEAD_SUMMARY,
    isHidden: false,
    isShowingInNavigation: false,
    path: STEP_EQ_LEAD_SUMMARY,
    zodSchema: z.object({
      consent: z.boolean().refine((value) => value, 'Consent is required to proceed.'),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_EQ_LEAD_SUMMARY].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  // BCM Short Wizard Steps
  [STEP_BCM_2_HOMEPAGE]: {
    id: STEP_BCM_2_HOMEPAGE,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm',
    zodSchema: z.object({}),
    isComplete: (state) => true,
  },
  // Optional post-signup add-a-card step (billed cohort). Gated at runtime
  // by billing-config.applyCardStepEnabled — with the flag off the step
  // component forwards straight to contact-soon, so it is safe to keep in
  // the wizard sequence unconditionally. Empty schema + always complete:
  // it must never trap farthest-step / first-not-valid navigation.
  [STEP_BCM_2_ADD_CARD]: {
    id: STEP_BCM_2_ADD_CARD,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm/app/add-card',
    zodSchema: z.object({}),
    isComplete: () => true,
  },
  [STEP_BCM_2_END_AS_WILL_CONTACT]: {
    id: STEP_BCM_2_END_AS_WILL_CONTACT,
    isHidden: false,
    isShowingInNavigation: false,
    // Must match the BcmRouter route — the wizard's raw pushState puts this
    // in the address bar, and a refresh on a path without a Route goes blank.
    path: '/bcm/app/contact-soon',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      business_ein: FIELD_VALIDATION.business_ein,
    }),
    isComplete: (state) => true,
  },
  [STEP_BCM_2_INFO]: {
    id: STEP_BCM_2_INFO,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm/app/info',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      owner_1_first: FIELD_VALIDATION.owner_1_first,
      owner_1_last: FIELD_VALIDATION.owner_1_last,
      owner_1_mobile: FIELD_VALIDATION.owner_1_mobile,
      owner_1_email: FIELD_VALIDATION.owner_1_email,
      consent: z.boolean().refine((value) => value, 'Consent is required to proceed.'),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BCM_2_INFO].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_BCM_2_BUSINESS_MORE_DETAILS]: {
    id: STEP_BCM_2_BUSINESS_MORE_DETAILS,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm/app/business/more-details',
    zodSchema: z.object({
      business_street: FIELD_VALIDATION.business_street,
      business_suite: FIELD_VALIDATION.business_suite,
      business_city: FIELD_VALIDATION.business_city,
      business_state: FIELD_VALIDATION.business_state,
      business_zip: FIELD_VALIDATION.business_zip,
      business_ein: FIELD_VALIDATION.business_ein,
      business_years: FIELD_VALIDATION.business_years,
      owner_1_title: FIELD_VALIDATION.owner_1_title,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BCM_2_BUSINESS_MORE_DETAILS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  // BCM Wizard Steps
  [STEP_BCM_HOMEPAGE]: {
    id: STEP_BCM_HOMEPAGE,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm2',
    zodSchema: z.object({}),
    isComplete: (state) => true,
  },
  [STEP_BCM_SUMMARY]: {
    id: STEP_BCM_SUMMARY,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm2/app/summary',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      business_ein: FIELD_VALIDATION.business_ein,
    }),
    isComplete: (state) => true,
  },
  [STEP_BCM_INFO]: {
    id: STEP_BCM_INFO,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm2/app/info',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      owner_1_first: FIELD_VALIDATION.owner_1_first,
      owner_1_last: FIELD_VALIDATION.owner_1_last,
      owner_1_mobile: FIELD_VALIDATION.owner_1_mobile,
      owner_1_email: FIELD_VALIDATION.owner_1_email,
      consent: z.boolean().refine((value) => value, 'Consent is required to proceed.'),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_INFO].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_INTEGRATED_LOGIN]: {
    id: STEP_INTEGRATED_LOGIN,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/login',
    zodSchema: z.object({}),
    isComplete: (state) => true,
  },
  [STEP_BCM_BUSINESS_MORE_DETAILS]: {
    id: STEP_BCM_BUSINESS_MORE_DETAILS,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/bcm2/app/business/more-details',
    zodSchema: z.object({
      business_street: FIELD_VALIDATION.business_street,
      business_suite: FIELD_VALIDATION.business_suite,
      business_city: FIELD_VALIDATION.business_city,
      business_state: FIELD_VALIDATION.business_state,
      business_zip: FIELD_VALIDATION.business_zip,
      business_ein: FIELD_VALIDATION.business_ein,
      business_years: FIELD_VALIDATION.business_years,
      owner_1_title: FIELD_VALIDATION.owner_1_title,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  // Apply Wizard Steps
  [STEP_HOMEPAGE]: {
    id: STEP_HOMEPAGE,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/',
    zodSchema: z.object({}),
    isComplete: (state) => true,
  },
  [STEP_AMOUNT]: {
    id: STEP_AMOUNT,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/app/amount',
    zodSchema: z.object({
      loan_amount: FIELD_VALIDATION.loan_amount,
      loan_terms: FIELD_VALIDATION.loan_terms,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_AMOUNT].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_INFO]: {
    id: STEP_INFO,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/app/info',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      owner_1_first: FIELD_VALIDATION.owner_1_first,
      owner_1_last: FIELD_VALIDATION.owner_1_last,
      owner_1_mobile: FIELD_VALIDATION.owner_1_mobile,
      owner_1_email: FIELD_VALIDATION.owner_1_email,
      consent: z.boolean().refine((value) => value, 'Consent is required to proceed.'),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_INFO].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_VERIFY_LOGIN]: {
    id: STEP_VERIFY_LOGIN,
    isHidden: false,
    isShowingInNavigation: false,
    path: '/app/verify/login',
    zodSchema: z.object({}),
    isComplete: (state) => {
      try {
        return (
          (state.business_name /* previous step is filled anyhow */ ||
            state.business_street) /* next step is filled anyhow */ &&
          !!ApplyWizardSteps[STEP_VERIFY_LOGIN].zodSchema.parse(state)
        );
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_BUSINESS_MORE_DETAILS]: {
    id: STEP_BUSINESS_MORE_DETAILS,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/business/more-details',
    zodSchema: z.object({
      business_street: FIELD_VALIDATION.business_street,
      business_suite: FIELD_VALIDATION.business_suite,
      business_city: FIELD_VALIDATION.business_city,
      business_state: FIELD_VALIDATION.business_state,
      business_zip: FIELD_VALIDATION.business_zip,
      home_based: FIELD_VALIDATION.home_based,
      business_website: FIELD_VALIDATION.business_website,
      business_years: FIELD_VALIDATION.business_years,
      business_structure: FIELD_VALIDATION.business_structure,
      business_naics: FIELD_VALIDATION.business_naics,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_BANK_CONNECT]: {
    id: STEP_BANK_CONNECT,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/bank-connect',
    zodSchema: z.object({}),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BANK_CONNECT].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_BUSINESS_FURTHER_DETAILS]: {
    id: STEP_BUSINESS_FURTHER_DETAILS,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/business/further-details',
    zodSchema: z.object({
      business_ein: FIELD_VALIDATION.business_ein,
      business_revenue: FIELD_VALIDATION.business_revenue,
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BUSINESS_FURTHER_DETAILS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_BUSINESS_OWNERS]: {
    id: STEP_BUSINESS_OWNERS,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/business/owners',
    zodSchema: {
      parse: (data: { [a: string]: any }) => {
        const ownersCount = getNumberOfOwnersFromFormValues(data);
        let schema: { [a: string]: any } = {};
        for (let i = 1; i <= ownersCount; i++) {
          schema[`owner_${i}_ssn`] = FIELD_VALIDATION.owner_ssn;
          schema[`owner_${i}_title`] = FIELD_VALIDATION.owner_1_title;
          schema[`owner_${i}_ownership`] = FIELD_VALIDATION.owner_ownership;
          schema[`owner_${i}_dob_day`] = FIELD_VALIDATION.owner_dob_day;
          schema[`owner_${i}_dob_month`] = FIELD_VALIDATION.owner_dob_month;
          schema[`owner_${i}_dob_year`] = FIELD_VALIDATION.owner_dob_year;
          schema[`owner_${i}_street`] = FIELD_VALIDATION.owner_1_street;
          schema[`owner_${i}_suite`] = FIELD_VALIDATION.owner_1_suite;
          schema[`owner_${i}_city`] = FIELD_VALIDATION.owner_1_city;
          schema[`owner_${i}_state`] = FIELD_VALIDATION.owner_1_state;
          schema[`owner_${i}_zip`] = FIELD_VALIDATION.owner_1_zip;
          if (i > 1) {
            schema[`owner_${i}_mobile`] = FIELD_VALIDATION.owner_1_mobile;
            schema[`owner_${i}_email`] = FIELD_VALIDATION.owner_1_email;
            schema[`owner_${i}_first`] = FIELD_VALIDATION.owner_1_first;
            schema[`owner_${i}_last`] = FIELD_VALIDATION.owner_1_last;
          }
        }
        return z
          .object(schema)
          .superRefine((values, ctx) => {
            // Each owner needs their OWN email. Two owner blocks sharing one
            // email fused both people into a single contact server-side
            // (LandTech incident 2026-09-03, Trello 1954): owner #2's data —
            // SSN included — overwrote owner #1's records.
            const seen = new Map<string, number>();
            for (let i = 1; i <= ownersCount; i++) {
              const raw = (data[`owner_${i}_email`] ?? values[`owner_${i}_email`]) as string | undefined;
              const email = raw?.trim().toLowerCase();
              if (!email) continue;
              const firstOwnerWithEmail = seen.get(email);
              if (firstOwnerWithEmail !== undefined) {
                ctx.addIssue({
                  code: 'custom',
                  path: [`owner_${i}_email`],
                  message: `Each owner needs their own email address — this one is already used by owner ${firstOwnerWithEmail}.`,
                });
              } else {
                seen.set(email, i);
              }
            }
          })
          .parse(data);
      },
    } as z.ZodType,
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_BUSINESS_OWNERS].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_TYPE_OF_FUNDING]: {
    id: STEP_TYPE_OF_FUNDING,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/type-of-funding',
    zodSchema: z.object({
      loan_type: z
        .array(z.string().refine(...stringWithContent()))
        .refine((val) => val && Array.isArray(val) && val.some((item) => item !== 'UNKNOWN'), {
          error: 'Please select at least one product type.',
        }),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_TYPE_OF_FUNDING].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_WHEN_FUNDING_NEEDED]: {
    id: STEP_WHEN_FUNDING_NEEDED,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/aproximate-timing',
    zodSchema: z.object({
      when_funding_needed: z.string().refine(...stringWithContent()),
    }),
    isComplete: (state) => {
      try {
        return !!ApplyWizardSteps[STEP_WHEN_FUNDING_NEEDED].zodSchema.parse(state);
      } catch (error) {
        return false;
      }
    },
  },
  [STEP_SUMMARY]: {
    id: STEP_SUMMARY,
    isHidden: false,
    isShowingInNavigation: true,
    path: '/app/funding-is-almost-here',
    zodSchema: z.object({
      business_name: FIELD_VALIDATION.business_name,
      business_ein: FIELD_VALIDATION.business_ein,
      business_revenue: FIELD_VALIDATION.business_revenue,
      loan_amount: FIELD_VALIDATION.loan_amount,
      loan_terms: FIELD_VALIDATION.loan_terms,
      business_years: FIELD_VALIDATION.business_years,
    }),
    isComplete: (state) => {
      try {
        return (
          /* previous step is filled anyhow */ state.owner_1_dob_day &&
          state.loan_type &&
          !!ApplyWizardSteps[STEP_HOMEPAGE].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_AMOUNT].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_INFO].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_VERIFY_LOGIN].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_BUSINESS_FURTHER_DETAILS].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_BUSINESS_OWNERS].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_BANK_CONNECT].zodSchema.parse(state) &&
          !!ApplyWizardSteps[STEP_TYPE_OF_FUNDING].zodSchema.parse(state)
        );
      } catch (error) {
        return false;
      }
    },
  },
};
